import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatDateDisplay } from "../../lib/utils";
import { toast } from "sonner";
import { Plus, Trash2, ToggleLeft, ToggleRight, Tag, X, Pencil } from "lucide-react";

const CATEGORY_OPTIONS = [
  { slug: "games", label: "Games" },
  { slug: "consoles", label: "Consoles" },
  { slug: "accessories", label: "Accessories" },
  { slug: "repair-parts", label: "Repair Parts" },
  { slug: "digital", label: "Digital" },
];

const EMPTY_FORM = {
  code: "",
  type: "global",
  discount_type: "percent",
  discount_value: "",
  min_cart_value: "",
  max_uses: "",
  valid_until: "",
  selected_product_ids: [],
  selected_category_slugs: [],
  active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  const load = async () => {
    const r = await api.get("/admin/coupons");
    setCoupons(r.data);
  };

  useEffect(() => {
    load();
    api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data));
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      selected_product_ids: f.selected_product_ids.includes(id)
        ? f.selected_product_ids.filter((x) => x !== id)
        : [...f.selected_product_ids, id],
    }));
  };

  const toggleCategory = (slug) => {
    setForm((f) => ({
      ...f,
      selected_category_slugs: f.selected_category_slugs.includes(slug)
        ? f.selected_category_slugs.filter((x) => x !== slug)
        : [...f.selected_category_slugs, slug],
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setProductSearch("");
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      code: c.code,
      type: c.type,
      discount_type: c.discount_type,
      discount_value: String(c.discount_value),
      min_cart_value: String(c.min_cart_value || ""),
      max_uses: String(c.max_uses || ""),
      valid_until: c.valid_until || "",
      selected_product_ids: c.product_ids || [],
      selected_category_slugs: c.category_slugs || [],
      active: c.active,
    });
    setProductSearch("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setProductSearch("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        discount_type: form.discount_type,
        discount_value: parseFloat(form.discount_value) || 0,
        min_cart_value: parseFloat(form.min_cart_value) || 0,
        max_uses: parseInt(form.max_uses) || 0,
        valid_until: form.valid_until || null,
        product_ids: form.type === "product" ? form.selected_product_ids : [],
        category_slugs: form.type === "category" ? form.selected_category_slugs : [],
        active: form.active,
      };

      if (editingId) {
        await api.patch(`/admin/coupons/${editingId}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/admin/coupons", { ...payload, code: form.code });
        toast.success("Coupon created");
      }
      closeForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save coupon");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
    await load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await api.delete(`/admin/coupons/${c.id}`);
    toast.success("Coupon deleted");
    await load();
  };

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Promotions</div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tighter">Coupons</h1>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full hover:bg-gold-light btn-glow transition-all"
        >
          <Plus className="w-3.5 h-3.5" /> New Coupon
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-navy-700 rounded-xl neon-border p-6 mb-8 space-y-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">
            {editingId ? "Edit Coupon" : "Create Coupon"}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Code — read-only when editing */}
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Code</label>
              {editingId ? (
                <div className="w-full mt-1 bg-navy-900/60 border border-gold/10 rounded-md px-3 py-2.5 text-sm font-mono text-gold">
                  {form.code}
                </div>
              ) : (
                <input
                  name="code"
                  value={form.code}
                  onChange={onChange}
                  placeholder="SUMMER20"
                  required
                  className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60"
                />
              )}
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Type</label>
              <select name="type" value={form.type} onChange={onChange} className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60">
                <option value="global">Global (all products)</option>
                <option value="product">Product-specific</option>
                <option value="category">Category-specific</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Discount Type</label>
              <select name="discount_type" value={form.discount_type} onChange={onChange} className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60">
                <option value="percent">Percentage (%)</option>
                <option value="flat">Flat Amount (₹)</option>
              </select>
            </div>

            <FormField
              label={form.discount_type === "percent" ? "Discount %" : "Discount ₹"}
              name="discount_value"
              type="number"
              value={form.discount_value}
              onChange={onChange}
              placeholder={form.discount_type === "percent" ? "10" : "200"}
              required
            />

            {form.type === "global" && (
              <FormField label="Min Cart Value (₹)" name="min_cart_value" type="number" value={form.min_cart_value} onChange={onChange} placeholder="0" />
            )}

            <FormField label="Max Uses (0 = unlimited)" name="max_uses" type="number" value={form.max_uses} onChange={onChange} placeholder="0" />
            <DateInput label="Valid Until" value={form.valid_until} onChange={onChange} />

            {form.type === "product" && (
              <div className="sm:col-span-2 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Select Products</div>
                {form.selected_product_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {form.selected_product_ids.map((id) => {
                      const prod = products.find((p) => p.id === id);
                      return (
                        <span key={id} className="flex items-center gap-1 bg-gold/20 text-gold text-[11px] font-semibold px-2 py-1 rounded-full">
                          {prod?.name || id.slice(0, 8) + "…"}
                          <button type="button" onClick={() => toggleProduct(id)} className="hover:text-rose-400 transition">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <input
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search products…"
                  className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
                />
                <div className="max-h-44 overflow-y-auto border border-gold/10 rounded-lg divide-y divide-gold/10">
                  {products
                    .filter((p) => !productSearch.trim() || p.name.toLowerCase().includes(productSearch.toLowerCase()))
                    .slice(0, 30)
                    .map((p) => {
                      const selected = form.selected_product_ids.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => toggleProduct(p.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition ${selected ? "bg-gold/10 text-gold" : "hover:bg-navy-700/60 text-slate-300"}`}
                        >
                          {p.images?.[0] && <img src={p.images[0]} alt="" className="w-8 h-8 object-cover rounded shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{p.name}</div>
                            <div className="text-[10px] font-mono text-slate-500">{p.id}</div>
                          </div>
                          {selected && <span className="text-[10px] font-black text-gold">✓</span>}
                        </button>
                      );
                    })}
                  {products.filter((p) => !productSearch.trim() || p.name.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-3 text-xs text-slate-500">No products match.</div>
                  )}
                </div>
                <p className="text-[10px] text-slate-500">Coupon applies when the cart contains at least one selected product.</p>
              </div>
            )}

            {form.type === "category" && (
              <div className="sm:col-span-2 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Select Categories</div>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map(({ slug, label }) => {
                    const selected = form.selected_category_slugs.includes(slug);
                    return (
                      <button
                        key={slug}
                        type="button"
                        onClick={() => toggleCategory(slug)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition ${
                          selected
                            ? "bg-gold/20 border-gold text-gold"
                            : "bg-navy-800 border-gold/20 text-slate-400 hover:border-gold/50"
                        }`}
                      >
                        {selected && <span>✓</span>}
                        {label}
                      </button>
                    );
                  })}
                </div>
                {form.selected_category_slugs.length === 0 && (
                  <p className="text-[10px] text-rose-400">Select at least one category.</p>
                )}
                <p className="text-[10px] text-slate-500">Coupon applies to all products in the selected categories.</p>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" name="active" checked={form.active} onChange={onChange} className="accent-gold" />
            <span className="text-slate-300">Active (can be used immediately)</span>
          </label>

          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-6 py-2.5 rounded-full hover:bg-gold-light transition-all disabled:opacity-50">
              {saving ? (editingId ? "Saving…" : "Creating…") : (editingId ? "Save Changes" : "Create Coupon")}
            </button>
            <button type="button" onClick={closeForm} className="text-slate-400 hover:text-white text-xs uppercase tracking-wider px-4 py-2.5">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-navy-700 rounded-xl neon-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Type</th>
              <th className="text-left p-4">Discount</th>
              <th className="text-left p-4">Min Cart</th>
              <th className="text-left p-4">Uses</th>
              <th className="text-left p-4">Valid Until</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.id} className="border-t border-gold/10">
                <td className="p-4 font-mono font-bold text-gold">
                  <div className="flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5 shrink-0" />
                    {c.code}
                  </div>
                </td>
                <td className="p-4 text-xs">
                  {c.type === "product" ? (
                    <div>
                      <span className="bg-violet-500/20 text-violet-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Product</span>
                      {c.product_ids?.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {c.product_ids.map((pid) => {
                            const prod = products.find((p) => p.id === pid);
                            return (
                              <div key={pid} className="text-[10px] text-slate-400 truncate max-w-[160px]">
                                {prod ? prod.name : <span className="font-mono">{pid.slice(0, 12)}…</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : c.type === "category" ? (
                    <div>
                      <span className="bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Category</span>
                      {c.category_slugs?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.category_slugs.map((slug) => (
                            <span key={slug} className="text-[10px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded capitalize">{slug}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="bg-sky-500/20 text-sky-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Global</span>
                  )}
                </td>
                <td className="p-4 font-bold">
                  {c.discount_type === "percent" ? `${c.discount_value}%` : `₹${c.discount_value}`}
                </td>
                <td className="p-4 text-slate-400">
                  {c.type === "product" || c.type === "category" ? <span className="text-slate-600">—</span> : `₹${c.min_cart_value || 0}`}
                </td>
                <td className="p-4 text-slate-400">
                  {c.used_count}
                  {c.max_uses > 0 && <span className="text-slate-500"> / {c.max_uses}</span>}
                </td>
                <td className="p-4 text-slate-400 text-xs">
                  {c.valid_until ? formatDateDisplay(c.valid_until) : "No expiry"}
                </td>
                <td className="p-4">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${c.active ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(c)} title="Edit" className="p-1.5 text-slate-400 hover:text-gold transition">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => toggleActive(c)} title={c.active ? "Deactivate" : "Activate"} className="p-1.5 text-slate-400 hover:text-gold transition">
                      {c.active ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => remove(c)} title="Delete" className="p-1.5 text-slate-400 hover:text-rose-400 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {coupons.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400">No coupons yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormField({ label, name, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input
        name={name}
        {...rest}
        className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60"
      />
    </label>
  );
}

// value/onChange use YYYY-MM-DD; displays and accepts DD-MM-YYYY
function DateInput({ label, value, onChange }) {
  const toDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    if (y && m && d) return `${d}-${m}-${y}`;
    return iso;
  };
  const [display, setDisplay] = React.useState(() => toDisplay(value));
  React.useEffect(() => { setDisplay(toDisplay(value)); }, [value]);

  const handle = (e) => {
    let raw = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let formatted = raw;
    if (raw.length > 4) formatted = raw.slice(0, 2) + "-" + raw.slice(2, 4) + "-" + raw.slice(4);
    else if (raw.length > 2) formatted = raw.slice(0, 2) + "-" + raw.slice(2);
    setDisplay(formatted);
    if (raw.length === 8) {
      onChange({ target: { name: "valid_until", value: `${raw.slice(4)}-${raw.slice(2, 4)}-${raw.slice(0, 2)}` } });
    } else {
      onChange({ target: { name: "valid_until", value: "" } });
    }
  };

  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
      <input
        value={display}
        onChange={handle}
        placeholder="DD-MM-YYYY"
        className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60"
      />
    </label>
  );
}
